import {
    getStoredFilters,
    usePersistFilterStorageKey,
    useSafeSessionStorage,
    useStorageKey,
} from '@/_shared/tools/storage.js';
import type { Column } from '@tanstack/react-table';
import { useCallback, useEffect } from 'react';
import { resetFilter } from '../../../../_shared/reset.js';
import type { StoredSelectedItemsSchema } from '../../../../types.js';
import { useOnGenericSelectionChange } from '../selection-change.js';

type GenericData = {
    allIds: string[];
    items: { id: string; value: string | number }[];
};

type UseListFilterParams<TData extends GenericData, TTableData, TTableValue> = {
    data: TData;
    disabled: boolean;
    tableId: string;
    column: Column<TTableData, TTableValue>;
    onResetDropdownValue: () => void;
    onRegularSelectionChange: (selectedValues: (string | number)[], items: string[]) => void;
};

export function useListFilter<TData extends GenericData, TTableData, TTableValue>({
    data,
    disabled,
    tableId,
    column,
    onResetDropdownValue,
    onRegularSelectionChange,
}: UseListFilterParams<TData, TTableData, TTableValue>) {
    const localStorageFilterKey = usePersistFilterStorageKey({
        id: tableId,
    });

    const sessionStorageKey = useStorageKey({
        base: tableId,
        utility: 'filter',
        id: column.id,
        version: 1,
    });

    const [selectedItems, setSelectedItems] = useSafeSessionStorage<StoredSelectedItemsSchema>(
        sessionStorageKey,
        () => ({
            current: data.allIds,
            persisted: data.allIds,
        })
    );

    const { handleSelectionChange } = useOnGenericSelectionChange({ data, selectedItems, setSelectedItems });

    const resetListFilter = useCallback(
        (resetDropdown: boolean) => {
            if (disabled) {
                return;
            }

            resetFilter(column);

            setSelectedItems({
                current: data.allIds,
                persisted: data.allIds,
            });

            if (resetDropdown) {
                onResetDropdownValue();
            }
        },
        [column, data.allIds, disabled, onResetDropdownValue, setSelectedItems]
    );

    const updateSelected = useCallback(
        (key: 'current' | 'persisted') => {
            if (disabled) {
                return;
            }

            const selectedValues = data.items
                .filter((item) => selectedItems[key].includes(item.id))
                .map((item) => item.value);

            const items = selectedItems[key];

            setSelectedItems({
                current: items,
                persisted: items,
            });

            if (items.includes('select-all')) {
                if (!(key === 'persisted' && getStoredFilters(localStorageFilterKey).length)) {
                    resetListFilter(true);
                }
            } else {
                onRegularSelectionChange(selectedValues, items);
            }
        },
        [
            data.items,
            disabled,
            localStorageFilterKey,
            selectedItems,
            onRegularSelectionChange,
            resetListFilter,
            setSelectedItems,
        ]
    );

    const onApplyFilter = useCallback(() => {
        updateSelected('current');
    }, [updateSelected]);

    // load the stored filters on initial load
    useEffect(() => {
        updateSelected('persisted');
    }, []);

    return {
        selectedItems,
        handleSelectionChange,
        onApplyFilter,
        resetListFilter,
    };
}
